import { useState, type ReactNode } from "react";

import { Check, Plus, X } from "lucide-react";

import { SUGGESTION_BUTTON_CLASS } from "../../components/SmartInputs";

import "../../components/CompanyManagementMobile.css";
import { STEPS } from "./model";
export function SecHead({ title, sub }: { title: string; sub?: string }) {
  return <div className="mb-4"><h2 tabIndex={-1} className="text-lg font-bold text-navy focus:outline-none">{title}</h2>{sub && <p className="text-sm text-mut mt-1">{sub}</p>}</div>;
}

export function CompanyProgress({ step, disabled, onStep }: { step: number; disabled: boolean; onStep: (step: number) => void }) {
  return <nav aria-label="Business registration progress" className="overflow-x-auto mb-6"><ol className="flex items-center w-max min-w-full">
    {STEPS.map((stage, index) => <li key={stage.t} className="flex items-center shrink-0">
      <button type="button" aria-label={`Step ${index + 1}: ${stage.t}`} aria-current={index === step ? "step" : undefined} disabled={disabled || index > step} onClick={() => onStep(index)} className={`flex min-h-11 min-w-11 items-center gap-1.5 sm:gap-2 rounded px-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2 ${index <= step ? "text-navy" : "text-mut"}`}>
        <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${index < step ? "bg-brand-green text-white" : index === step ? "bg-brand-red text-white" : "bg-navy-50 text-mut"}`}>{index < step ? <Check className="w-4 h-4" aria-hidden="true" /> : index + 1}</span>
        <span className="text-[11px] sm:text-xs font-semibold whitespace-nowrap">{stage.t}</span>
      </button>
      {index < STEPS.length - 1 && <span aria-hidden="true" className="w-2 sm:w-6 h-px bg-line sm:mx-2" />}
    </li>)}
  </ol></nav>;
}

export function OptionalSection({ title, children }: { title: string; children: ReactNode }) {
  return <section className="border-t border-line pt-4">
    <h3 className="text-sm font-semibold text-navy">{title} <span className="text-xs font-normal text-mut">(optional)</span></h3>
    <div className="space-y-4 pt-4">{children}</div>
  </section>;
}

export function BusinessOption({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return <button type="button" aria-pressed={selected} onClick={onClick} className={`${SUGGESTION_BUTTON_CLASS} inline-flex items-center gap-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2 aria-pressed:border-navy-500 aria-pressed:bg-navy-50 aria-pressed:font-semibold aria-pressed:text-navy`}>
    {selected ? <Check className="w-3 h-3 shrink-0" aria-hidden="true" /> : <Plus className="w-3 h-3 shrink-0 text-brand-red" aria-hidden="true" />}
    {label}
  </button>;
}

export function CheckChips({ options, value, onChange }: { options: string[]; value: string[]; onChange: (v: string[]) => void }) {
  const sel = new Set(value);
  const toggle = (o: string) => { const n = new Set(sel); n.has(o) ? n.delete(o) : n.add(o); onChange([...n]); };
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <BusinessOption key={o} label={o} selected={sel.has(o)} onClick={() => toggle(o)} />
      ))}
    </div>
  );
}

export function Chips({ value, onChange, placeholder, label, max = 40, draftValue, onDraftChange }: { label: string; draftValue?: string; onDraftChange?: (value: string) => void; value: string[]; onChange: (v: string[]) => void; placeholder?: string; max?: number }) {
  const [localDraft, setLocalDraft] = useState("");
  const draft = draftValue ?? localDraft;
  const setDraft = onDraftChange ?? setLocalDraft;
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (!value.includes(t) && value.length < max) onChange([...value, t]);
    setDraft("");
  };
  const remove = (t: string) => onChange(value.filter((x) => x !== t));
  return (
    <div>
      <div className="flex gap-2">
        <input aria-label={label} className="field flex-1" value={draft} placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} />
        <button type="button" aria-label={`Add ${label.toLowerCase()}`} onClick={add} disabled={value.length >= max} className="btn-outline h-10 px-3 disabled:opacity-50"><Plus className="w-4 h-4" /></button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {value.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 rounded-lg border border-line bg-navy-50 text-navy text-xs px-3 py-2">
              {t}<button type="button" aria-label={`Remove ${t}`} onClick={() => remove(t)} className="text-brand-red hover:text-brand-reddark"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      {value.length >= max && <p className="text-xs text-mut mt-1">Maximum of {max} reached.</p>}
    </div>
  );
}
